import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-6 text-cyan-600 hover:text-cyan-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>
        
        <Card className="shadow-lg">
          <CardContent className="p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Terms of Service</h1>
            <p className="text-slate-500 mb-8">Last Updated: February 2025</p>
            
            <div className="space-y-8 text-slate-700">
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Agreement to Terms</h2>
                <p>
                  By booking a service with Carpex Cleaning Services ("we," "our," or "us"), you agree to these Terms of Service. 
                  Please read them carefully before scheduling any cleaning services.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Services Offered</h2>
                <p className="mb-3">Carpex Cleaning Services provides professional cleaning services including:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Carpet Cleaning</li>
                  <li>Upholstery Cleaning</li>
                  <li>Mattress Cleaning</li>
                  <li>Area Rug Cleaning</li>
                  <li>Commercial Cleaning Services</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Booking and Appointments</h2>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>All bookings are subject to availability and confirmation by our team.</li>
                  <li>We will contact you within 24-48 hours to confirm your appointment.</li>
                  <li>Accurate property information must be provided at the time of booking.</li>
                  <li>We reserve the right to adjust pricing based on actual conditions found on-site.</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Pricing and Payment</h2>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Prices quoted are estimates based on information provided.</li>
                  <li>Final pricing may vary based on actual square footage, condition, and additional services required.</li>
                  <li>Payment is due upon completion of services unless otherwise arranged.</li>
                  <li>We accept cash, credit cards, and e-transfers.</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Cancellation Policy</h2>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Cancellations made 24 hours or more before the appointment: No charge.</li>
                  <li>Cancellations made less than 24 hours before the appointment: A cancellation fee may apply.</li>
                  <li>No-shows without prior notice may be subject to a service call fee.</li>
                  <li>We reserve the right to cancel or reschedule appointments due to unforeseen circumstances.</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Customer Responsibilities</h2>
                <p className="mb-3">Before our arrival, customers should:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Remove small items, toys, and fragile objects from areas to be cleaned.</li>
                  <li>Vacuum loose debris if possible.</li>
                  <li>Inform us of any existing damage, stains, or areas of concern.</li>
                  <li>Ensure safe access to the property and work areas.</li>
                  <li>Secure pets in a separate area during the service.</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Liability and Limitations</h2>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We carry liability insurance for our services.</li>
                  <li>We are not responsible for pre-existing damage, wear, or deterioration of items.</li>
                  <li>Some stains may not be fully removable due to their nature or age.</li>
                  <li>We are not liable for color fading or texture changes in older or damaged materials.</li>
                  <li>Claims for damages must be reported within 24 hours of service completion.</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Satisfaction Guarantee</h2>
                <p>
                  We stand behind our work. If you are not satisfied with our service, please contact us within 
                  24 hours and we will address your concerns promptly. We may offer a re-clean of affected areas 
                  at no additional charge.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Health and Safety</h2>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We use eco-friendly and safe cleaning products.</li>
                  <li>Please inform us of any allergies or sensitivities before service.</li>
                  <li>Carpets and upholstery may require drying time after cleaning (typically 2-6 hours).</li>
                  <li>Ensure proper ventilation after cleaning services.</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Contact Information</h2>
                <p className="mb-3">For questions about these Terms of Service, please contact us:</p>
                <ul className="list-none space-y-1 ml-4">
                  <li><strong>Phone:</strong> (825) 583-7478</li>
                  <li><strong>Email:</strong> infocarpexcleaning@gmail.com</li>
                  <li><strong>Hours:</strong> Monday - Sunday, 8AM - 6PM</li>
                </ul>
              </section>
              
              <section className="pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  By using our services, you acknowledge that you have read, understood, and agree to be bound 
                  by these Terms of Service. We reserve the right to modify these terms at any time without prior notice.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
